import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { DetailsConfigActions } from '../../AdminPanel/DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../../AdminPanel/DataDisplay/ExitConfigBlocker';
import { ConnectionDetailsSection } from '../components/ConnectionDetailsSection';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { LabeledTextField } from '../components/LabeledTextField';
import { SettingsCard } from '../components/SettingsCard';
import { AllVueFormValues } from './allVueConfig.schema';
import { ConnectionMethodCards } from './ConnectionMethodCards';
import { useAllVueConfigFormState } from './useAllVueConfigFormState';

export function AllVueConfiguration() {
  const {
    editStatus,
    isConnected,
    serverConnectionType,
    isLoading,
    successMessage,
    errorMessage,
    methods,
    onSave,
    onReset,
  } = useAllVueConfigFormState();

  const connectionType = methods.watch('connectionType');
  const description =
    'Configure your AllVue integration connection. Changes apply to your entire organization immediately after saving.';

  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' p='12px' gap='1rem' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <IntegrationDetailHeader appName={AppName.AllVue} description={description} />

        {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <SettingsCard>
          <ConnectionDetailsSection isConnected={isConnected} />

          <Box>
            <Typography variant='subtitle1' component='h6' sx={{ mb: '0.5rem', fontWeight: 500 }}>
              Connection Method
            </Typography>
            <ConnectionMethodCards />
          </Box>

          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            {connectionType === 'snowflake' ? (
              <SnowflakeFields />
            ) : (
              <OAuthFields isConnected={isConnected} serverConnectionType={serverConnectionType} />
            )}
          </Box>

          <Stack direction='row' gap='1rem' alignItems='center'>
            <DetailsConfigActions
              save={{ handler: onSave, disabled: editStatus !== 'modified' }}
              reset={{ handler: onReset }}
            />
          </Stack>
        </SettingsCard>
      </Stack>
    </FormProvider>
  );
}

function SnowflakeFields() {
  const {
    control,
    formState: { errors },
  } = useFormContext<AllVueFormValues>();

  return (
    <Controller
      name='database'
      control={control}
      render={({ field }) => (
        <LabeledTextField
          {...field}
          label='Database Name (where AllVue data is available)'
          required
          placeholder='e.g., ALLVUE_DB'
          error={!!errors.database}
          helperText={errors.database?.message}
        />
      )}
    />
  );
}

function OAuthFields({
  isConnected,
  serverConnectionType,
}: {
  isConnected: boolean;
  serverConnectionType: string;
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext<AllVueFormValues>();

  const isExistingOAuth = isConnected && serverConnectionType === 'oauth';

  return (
    <Stack gap='1rem'>
      <Controller
        name='clientId'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='Client ID'
            required
            placeholder='Enter your AllVue Client ID'
            error={!!errors.clientId}
            helperText={errors.clientId?.message}
          />
        )}
      />
      <Controller
        name='clientSecret'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='Client Secret'
            description={
              isExistingOAuth
                ? 'Leave blank to keep existing secret. Enter a new value to update it.'
                : 'Securely stored.'
            }
            required={!isExistingOAuth}
            type='password'
            placeholder={
              isExistingOAuth ? 'Leave blank to keep existing secret' : 'Enter your AllVue Client Secret'
            }
            error={!!errors.clientSecret}
            helperText={errors.clientSecret?.message}
          />
        )}
      />
      <Controller
        name='tokenEndpoint'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='Token Endpoint (Leave blank to use the default AllVue OAuth endpoint)'
            placeholder='https://api.allvue.com/oauth/token'
            error={!!errors.tokenEndpoint}
            helperText={errors.tokenEndpoint?.message}
          />
        )}
      />
    </Stack>
  );
}
